<template>
	<div class="integration-configuration">
		<div class="step-container">
			<div class="step-header">
				{{ t('common_step_number', { Step: 1 }) }} -
				{{ t('admin_integrations_select_platform') }}
			</div>
			<div class="step-content">
				<div class="description">
					{{ t('admin_integrations_platform_desc') }}
				</div>
				<Dropdown
					:options="platforms"
					v-model="selectedPlatform"
					global
					class="platform-dropdown"
					:loading="isLoadingPlatforms"
					@change="platformChange"
				/>
			</div>
		</div>

		<div class="step-container" v-if="selectedPlatform">
			<div class="step-header">
				{{ t('common_step_number', { Step: 2 }) }} -
				{{ t('admin_integrations_begin_config') }}
			</div>
			<div class="step-content">
				<div class="description">
					<Button
						primary
						:loading="isConfiguringConnection"
						@click="beginConfiguration"
						v-if="!(connection && platformConfig)"
						>Create Connection</Button
					>
					<div v-if="connection && platformConfig" class="platform-config">
						<div>
							<span>Dynamic Registration URL:</span>
							{{ platformConfig.dynamic_registration_url }}
						</div>
						<div>
							<span>Platform Integration Key:</span>
							{{ platformConfig.platform_integration_key }}
						</div>
					</div>
				</div>
			</div>
		</div>

		<div
			v-if="
				selectedPlatform === 'brightspace' &&
				connection &&
				platformConfig &&
				!isLoadingConnection
			"
			class="step-container"
		>
			<div class="step-header">Step 3 - Dynamic Tool Registration</div>
			<div class="step-content">
				<div class="description">
					<ol type="1">
						<li>Log into Brightspace as your LMS Admin</li>
						<li>Click the Admin Tools gear and select Manage Extensibility</li>
						<li>Click LTI Advantage</li>
					</ol>
				</div>
				<div class="documentation-iframe" v-if="documentationUrl">
					<iframe
						:src="documentationUrl"
						:title="t('admin_integrations_configuration_instructions')"
						loading="lazy"
					/>
				</div>
				<div class="description">
					<ol type="1" start="4">
						<li>Click Register Tool</li>
						<li>Select Dynamic</li>
					</ol>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue'
import { Dropdown, Button, useAlert } from '@/components/common'
import {
	integrationGet,
	integrationPatch,
	integrationPost,
} from '@/api/integrations'
import { useI18n } from 'vue-i18n'
import { getDocumentationUrl } from '../documentation'

// Props
const props = defineProps({
	integrationId: { type: [String, Number], required: true },
	integrationObject: { type: Object, required: false },
	connectionId: { type: [String, Number], required: false },
	platform: { type: String, required: false },
})

// Utilities
const { t } = useI18n()
const alert = useAlert()

// Reactive State
const platforms = ref([])
const selectedPlatform = ref(null)
const isLoadingPlatforms = ref(false)
const platformConfig = ref(null)
const connection = ref(null)
const isConfiguringConnection = ref(false)

// Computed
const documentationUrl = computed(() =>
	getDocumentationUrl(selectedPlatform.value)
)

// Methods
const fetchPlatforms = async () => {
	const response = await integrationGet(
		`/${props.integrationObject?.type}/configure/platforms`
	)

	// Map the new API response structure to the expected format
	platforms.value = Object.entries(response.data).map(([key, value]) => ({
		id: key,
		label: value.label,
		config_url: value.config_url, // Store this in case you need it later
	}))
}

const createConnection = async () => {
	connection.value = await integrationPost(
		`/${props.integrationId}/connections`
	)
}

const updateConfiguration = async () => {
	await integrationPatch(
		`/${props.integrationObject?.type}/configure/${connection.value.id}`,
		{
			platform: selectedPlatform.value,
			// BLOAT: The server requires these properties to be included in the payload, Flip said to always pass true as that's the server default
			sync_enabled: true,
			launch_enabled: true,
		}
	)
}

const fetchPlatformConfiguration = async () => {
	platformConfig.value = (
		await integrationGet(
			`/${props.integrationObject?.type}/configure/${connection.value.id}/${selectedPlatform.value}`
		)
	).data
}

const beginConfiguration = async () => {
	isConfiguringConnection.value = true
	try {
		await createConnection()

		await updateConfiguration()

		await fetchPlatformConfiguration()
	} catch (e) {
		console.error(e)
		// The service localizes its own errors off the Accept-Language header the
		// integrations client sends, so surface its message rather than mapping
		// codes here (e.g. MAX_INTEGRATION_INSTALLS when the org is at its limit)
		alert.error(e?.response?.data?.message || t('apierrors_error_generic'))
	} finally {
		isConfiguringConnection.value = false
	}
}

// Lifecycle Hooks
onMounted(async () => {
	// we need to handle the case where the connection is already created
	await fetchPlatforms()

	if (props.connectionId) {
		connection.value = (
			await integrationGet(`/connections/${props.connectionId}`)
		).data
		selectedPlatform.value = props.platform
		await fetchPlatformConfiguration()
	}
})
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.integration-configuration {
	width: 100%;
	.step-container {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		border: solid 1px var(--neutral-200);
		border-radius: 8px;
		width: 100%;
		margin-bottom: 25px;

		.step-header {
			display: flex;
			flex-direction: row;
			align-items: center;
			background-color: #f9f9f9;
			border-bottom: solid 1px var(--neutral-200);
			padding: 19px 22px 11px 22px;
			font-family: 'Avenir-Heavy';
			font-size: 20px;
			color: var(--neutral-800);
			width: 100%;
			border-top-left-radius: 8px;
			border-top-right-radius: 8px;
		}

		.step-content {
			width: 100%;
			padding: 23px 22px 11px 22px;
			.description {
				font-family: 'Avenir-Roman';
				font-size: 16px;
				color: var(--neutral-600);
				margin-bottom: 20px;
			}
			.platform-dropdown {
				width: 325px;
			}
			.platform-config {
				font-family: 'Avenir-Heavy';
				color: var(--neutral-800);
				span {
					font-family: 'Avenir-Roman';
					color: var(--neutral-600);
				}
			}
			.documentation-iframe {
				width: 100%;
				height: 100%;
				border: solid 1px var(--neutral-200);
				border-radius: 8px;
				height: 450px;
				margin-bottom: 11px;
				iframe {
					border-radius: 8px;
					width: 100%;
					height: 100%;
				}
			}
		}
	}
}
</style>
